import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, BehaviorSubject, timer, from, throwError, EMPTY } from 'rxjs';
import {
  catchError,
    concatMap,
    filter,
    finalize,
    map,
    mergeMap,
    shareReplay,
    skip,
    switchMap,
    takeWhile,
    tap,
} from 'rxjs/operators';
import { LatLng } from 'leaflet';
// Models
import { ACTIVE_OPERATION_ALL, ARCHIVE_OPERATION, OPERATIONS,LAST_OPERATION_ALL, APOPERATIONS, LAST_UPDATE, NEAR_INFRASTRUCTURES, NEAR_URBAN_AREAS } from '../utils/routing/routes.model';
import { Filters, GlobalStateResume, FlightOperation, MultipleApiResponse, Infrastructure, UrbanAreasBasicInfo } from '@mocca-technologies/model';
// Utils
import { OperationUtils } from '../utils/operations.util';
import { CoreService } from './core.service';

@Injectable({
    providedIn: 'root'
})

export class OperationsService {
    private readonly DEBUG = false;
    private readonly REQUEST_LEN = 10;
    private readonly LIVE_INTERVAL = 10000;

    private waitingRequest = new BehaviorSubject(false);
    waitingRequest$ = this.waitingRequest.asObservable();

    private selectedOperationData = new BehaviorSubject<FlightOperation>(null);
    selectedOperationData$: Observable<FlightOperation> = this.selectedOperationData
        .asObservable()
        .pipe(shareReplay(1));

    /**
     * Stream of {@link GlobalStateResume} updates for a Live Operation currently selected.
     * (![alt text](media://liveUpdates.jpg)
     */
    liveDataUpdates$: Observable<GlobalStateResume> = this.selectedOperationData$.pipe(
        filter((operation) => operation?.isClosed === false),
        switchMap((operation) =>
            from(timer(0, 2500)).pipe(
                skip(1),
                map(() => operation),
                // Stops when selection is empty! (on unselect)
                takeWhile((operation) => operation !== null),
                switchMap((operation) => {
                    const httpParams = new HttpParams().set('operationId', operation.uniqueId);
                    return this.http.get<MultipleApiResponse>(LAST_UPDATE, {
                        params: httpParams,
                        observe: 'response',
                        withCredentials: true
                    }).pipe(
                      catchError((error: HttpErrorResponse) => {
                        console.warn('Live operation update poll failed:', error.status, error.statusText);
                        // Skip this polling cycle and continue with next timer tick
                        return EMPTY;
                      })
                    );
                }),
                filter((res: HttpResponse<MultipleApiResponse>) => OperationUtils.filterResponse(res?.body?.responseObject as GlobalStateResume)),
                map((res: HttpResponse<MultipleApiResponse>) => OperationUtils.projectResponse(res?.body?.responseObject as GlobalStateResume)),
            )
        ),
        shareReplay(1)
    );

    constructor(private http: HttpClient,
                private coreService: CoreService
    ) { }

    /**
     * Requests the last batch of operations.
     *
     * @param count: Number of operations
     * @param index: Index of operations from the last one
     * @param operationFilter: Filter values
     */
    getLastOperations(
        page: number,
        count = this.REQUEST_LEN,
        operationFilter?: Filters
    ): Observable<HttpResponse<MultipleApiResponse>> {
        const httpParams = new HttpParams()
            .set('count', count.toString())
            .set('index', (page * count).toString())
            .set(
                'filterParameters',
                JSON.stringify({
                    pilots: operationFilter?.pilots?.length > 0 ? operationFilter.pilots.map(user => user.uniqueId) : null,
                    vehicles: operationFilter?.vehicles?.length > 0 ? operationFilter.vehicles.map(vehicle => vehicle.uniqueId) : null,
                    startDate: operationFilter?.startDate ? new Date(operationFilter?.startDate).toISOString() : null,
                    endDate: operationFilter?.endDate ? new Date(operationFilter?.endDate).toISOString() : null,
                    archived: operationFilter?.archived || null,
                    position: operationFilter?.position || null,
                })
            );

        this.waitingRequest.next(true);

        return this.http
            .get<MultipleApiResponse>(LAST_OPERATION_ALL, {
              params: httpParams,
              observe: 'response',
              withCredentials: true
            })
            .pipe(
              finalize(() => this.waitingRequest.next(false)),
              catchError((error) => {
                return throwError(() => error);
              })
            );
    }

    getUpdatedLastOperations(
        page: number,
        count = this.REQUEST_LEN,
        interval = this.LIVE_INTERVAL,
        operationFilter?: Filters
    ): Observable<HttpResponse<MultipleApiResponse>> {
        const httpParams = new HttpParams()
            .set('count', count.toString())
            .set('index', (page * count).toString())
            .set(
                'filterParameters',
                JSON.stringify({
                    pilots: operationFilter?.pilots?.length > 0 ? operationFilter.pilots.map(user => user.uniqueId) : null,
                    vehicles: operationFilter?.vehicles?.length > 0 ? operationFilter.vehicles.map(vehicle => vehicle.uniqueId) : null,
                    startDate: operationFilter?.startDate ? new Date(operationFilter?.startDate).toISOString() : null,
                    endDate: operationFilter?.endDate ? new Date(operationFilter?.endDate).toISOString() : null,
                    archived: operationFilter?.archived || null,
                    position: operationFilter?.position || null,
                })
            );

        this.waitingRequest.next(true);

        return timer(0, interval).pipe(
            concatMap(() =>
                this.http
                .get<MultipleApiResponse>(LAST_OPERATION_ALL, {
                  params: httpParams,
                  observe: 'response',
                  withCredentials: true
                })
            ),
            catchError((error: HttpErrorResponse) => {
              return throwError(() => error);
            }),
        )
    }

    /**
     * Request which live operations are currently active every 'interval' ms.
     * @param interval: Amount of time between requests
     */
    getLiveOperations(interval = this.LIVE_INTERVAL): Observable<HttpResponse<MultipleApiResponse>> {
      const httpParams = new HttpParams().set('index', '0').set('count', '100'); // Get all live operations
        return timer(0, interval).pipe(
            concatMap(() =>
                this.http.get<MultipleApiResponse>(ACTIVE_OPERATION_ALL,
                  {
                    params: httpParams,
                    observe: 'response',
                    withCredentials: true
                })
            ),
            catchError((error: HttpErrorResponse) => {
              return throwError(() => error);
            })
        );
    }

    /**
     * Requests details from the operation selected. The returned data is used to show Operation path on maps and
     * display data properties on Details page.
     *
     * @param flightOperation: Flight Operation object from the selection made
     */
    getFullOperation(flightOperationUuid: string): Observable<FlightOperation> {
        const httpParams = new HttpParams().set('operationId', flightOperationUuid);

        this.waitingRequest.next(true);

        return this.http
            .get<MultipleApiResponse>(OPERATIONS, {
              params: httpParams,
              observe: 'response',
              withCredentials: true
            })
            .pipe(
                map((res: HttpResponse<MultipleApiResponse>) => {
                    const op = res?.body?.responseObject as FlightOperation;

                    return {
                        ...op,
                        flightData: (op.flightData ?? []).filter((el) =>
                            OperationUtils.filterResponse(el)
                        ),
                    } as FlightOperation;
                }),
                map((operation: FlightOperation) => ({
                    ...operation,
                    flightData: operation.flightData.map((el) =>
                        OperationUtils.projectResponse(el)
                    )
                })),
                map((operation: FlightOperation) => ({
                    ...operation,
                    flightPath: operation.flightData.map((el) => new LatLng(el.latitude, el.longitude))
                })),
                finalize(() => {
                    this.waitingRequest.next(false);
                }),
                catchError((error: HttpErrorResponse) => {
                    return throwError(() => error);
                })
            );
    }

    getFullOp(flightOperationUuid: string): Observable<HttpResponse<MultipleApiResponse>> {
        const httpParams = new HttpParams().set('operationId', flightOperationUuid);

        this.waitingRequest.next(true);

        return this.http.get<MultipleApiResponse>(OPERATIONS, {
          params: httpParams,
          observe: 'response',
          withCredentials: true
        }).pipe(
          catchError((error: HttpErrorResponse) => {
            return throwError(() => error);
          }),
        );
    }

    /**
     * Hides the Operation selected from user interface.
     * If the user is 'devel' or 'admin' then the operation shows up labeled as an Archived one.
     *
     * @param flightOperation: Flight Operation object from the selection made
     */
    archiveCurrentOperation(): Observable<any> {
        return this.selectedOperationData$.pipe(
            map((operation) => {
                if (operation.isClosed) {
                    return operation;
                } else {
                    throw new Error("You can't archive live operations");
                }
            }),
            mergeMap((operation) => {
                const params = { operationId: operation.uniqueId };
                return this.http
                    .patch(ARCHIVE_OPERATION, {}, {
                        observe: 'response',
                        withCredentials: true,
                        params: params
                    })
                    .pipe(
                      map((res: HttpResponse<MultipleApiResponse>) => ({
                        uniqueId: (res?.body?.responseObject as FlightOperation).uniqueId,
                        response: res
                      })),
                      catchError((error: HttpErrorResponse) => {
                        return throwError(() => error);
                      })
                    );
            })
        );
    }

    /**
     * Empty current context from {@link OperationsService}. It's used only when Logged out.
     */
    emptyAccountSharedData(): void {
        this.selectedOperationData.next(null);
    }

    createAutopilotOperation(body: string): Observable<HttpResponse<MultipleApiResponse>> {
        return this.http.post<MultipleApiResponse>(APOPERATIONS, body, {
            observe: 'response',
            withCredentials: true
        }).pipe(
            tap((res: HttpResponse<MultipleApiResponse>) => {return res; }),
            catchError((error: HttpErrorResponse) => {
                return throwError(() => error);
            })
        );
    }

    setSelectedOperation(operation: FlightOperation) {
        this.selectedOperationData.next(operation);
    }

    /**
     * Given a location and a radius (meters), it returns the list of infrastructures that are within that radius.
     * @param lat latitude of the center point
     * @param lng longitude of the center point
     * @param radius in meters
     * @returns Observable<HttpResponse<MultipleApiResponse>> with responseObject as Infrastructure[]
     */
    getNearInfrastructuresBasicInfo(lat: number, lng: number, radius: number, username: string, password: string): Observable<HttpResponse<MultipleApiResponse>> {
        const httpParams = new HttpParams()
            .set('latitude', lat.toString())
            .set('longitude', lng.toString())
            .set('radius', radius.toString())
            .set('username', username)
            .set('password', password);

        return this.http.get<MultipleApiResponse>(NEAR_INFRASTRUCTURES, {
            params: httpParams,
            observe: 'response',
            withCredentials: true
        }).pipe(
            catchError((error: HttpErrorResponse) => {
                return throwError(() => error);
            })
        );
    }

    /**
     * Given a location and a radius (meters), it returns the list of urban areas that are within that radius.
     * @param lat latitude of the center point
     * @param lng longitude of the center point
     * @param radius in meters
     * @returns Observable<HttpResponse<MultipleApiResponse>> with responseObject as UrbanAreasBasicInfo[]
     */
    getNearUrbanAreasBasicInfo(lat: number, lng: number, radius: number, username: string, password: string): Observable<HttpResponse<MultipleApiResponse>> {
        const httpParams = new HttpParams()
            .set('latitude', lat.toString())
            .set('longitude', lng.toString())
            .set('radius', radius.toString())
            .set('username', username)
            .set('password', password);

        return this.http.get<MultipleApiResponse>(NEAR_URBAN_AREAS, {
            params: httpParams,
            observe: 'response',
            withCredentials: true
        }).pipe(
            catchError((error: HttpErrorResponse) => {
                return throwError(() => error);
            })
        );
    }
}
